import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { AssistenciaTecnicaForm, AssistenciaTecnicaSchema } from "../schemas/AssistenciaTecnica_cadastro/assistencia_tecnica";
import { AssistenciaTecnicaDetalhe } from "../../domain/models/assistencia_tecnica_detalhe";
import { AssistenciaTecnicaAtendimentoLista } from "../../domain/models/assistencia_tecnica_atendimento_lista";
import { AssistenciaTecnicaPesquisaLista } from "../../domain/models/assistencia_tecnica_pesquisa_lista";
import { AssistenciaTecnicaArquivoLista } from "../../domain/models/assistencia_tecnica_arquivo_lista";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useLocation } from "react-router";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useAssistenciaTecnicaCadastroPagination } from "../hooks/AssistenciaTecnica_cadastro/pagination";
import { ClienteCentroCustoDropDown } from "../../../../common/domain/models/cliente_centro_custo_dropdown";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { RegistroAtendimentoForm } from "../schemas/AssistenciaTecnica_cadastro/registro_atendimento";
import { PesquisaSatisfacaoDropDown } from "../../domain/models/pesquisa_satisfacao_dropdown";
import { PesquisaSatisfacaoForm } from "../schemas/AssistenciaTecnica_cadastro/pesquisa_satisfacao";
import { ArquivoDiversoForm } from "../schemas/AssistenciaTecnica_cadastro/arquivo_diverso";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { ItemPesquisaClienteLista } from "../../domain/models/item_pesquisa_cliente_lista";
import { AssistenciaPesquisaSatisfacaoForm } from "../schemas/AssistenciaTecnica_cadastro/assistencia_pesquisa_satisfacacao";

import dayjs from "dayjs";

/**
 * LoadAssistenciaTecnicaRegistrosAtendimentoFuncState
 */

type LoadAssistenciaTecnicaRegistrosAtendimentoFuncState = {
  id?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * LoadAssistenciaTecnicaPesquisasSatisfacaoFuncState
 */

type LoadAssistenciaTecnicaPesquisasSatisfacaoFuncState = {
  id?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * LoadAssistenciaTecnicaArquivosFuncState
 */

type LoadAssistenciaTecnicaArquivosFuncState = {
  id?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * LoadAssistenciaTecnicaCategoriasFuncState
 */

type LoadAssistenciaTecnicaCategoriasFuncState = {
  id?: number;
};

/**
 * useAssistenciaTecnicaCadastroViewModel
 */

export const useAssistenciaTecnicaCadastroViewModel = () => {

  const {
    entregaObras: {
      getPesquisaSatisfacaoDropDownUseCase,
      getAssistenciaTecnicaByIdUseCase,
      getAllAssistenciaRegistroAtendimentoUseCase,
      getAllAssistenciaPesquisaSatisfacaoUseCase,
      getAllAssistenciaArquivoDiversoUseCase,
      getAllAssistenciaCategoriaUseCase,
      createAssistenciaTecnicaUseCase,
      updateAssistenciaTecnicaUseCase,
      createAssistenciaRegistroAtendimentoUseCase,
      updateAssistenciaRegistroAtendimentoUseCase,
      deleteAssistenciaRegistroAtendimentoUseCase,
      createAssistenciaPesquisaSatisfacaoUseCase,
      deleteAssistenciaPesquisaSatisfacaoUseCase,
      createAssistenciaArquivoDiversoUseCase,
      updateAssistenciaArquivoDiversoUseCase,
      deleteAssistenciaArquivoDiversoUseCase,
      downloadAssistenciaArquivoDiversoUseCase,
      deleteAssistenciaTecnicaUseCase,
      getAllItemPesquisaClienteUseCase,
      updatePesquisaSatisfacaoClienteUseCase,
    },
    commons: {
      getAllLogAlteracaoByParentUseCase,
      getAllLogAlteracaoUseCase,
    },
    reports: {
      getReportAssistenciaTecnicaBasicoUseCase,
      getReportAssistenciaTecnicaCompletoUseCase,
    }
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
    loadClienteCentroCustoDropDown,
    loadFuncionariosDropDown,
  } = useDropDownLoader();

  const { pushInfoNotification, pushErrorNotification, setPageSaving } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idAssistenciaTecnica, setIdAssistenciaTecnica] = useState<number>();

  const [fillCliente, setFillCliente] = useState(false);

  const [assistenciaTecnica, setAssistenciaTecnica] = useState<AssistenciaTecnicaDetalhe>();
  const [registrosAtendimentos, setRegistrosAtendimentos] = useState<AssistenciaTecnicaAtendimentoLista[]>();
  const [pesquisasSatisfacao, setPesquisasSatisfacao] = useState<AssistenciaTecnicaPesquisaLista[]>();
  const [arquivos, setArquivos] = useState<AssistenciaTecnicaArquivoLista[]>();

  const [centroCustoDropdown, setCentroCustoDropdown] = useState<CentroCustoDropDown[]>();
  const [clientesDropdown, setClientesDropdown] = useState<ClienteCentroCustoDropDown[]>();
  const [filteredClientesDropdown, setFilteredClientesDropdown] = useState<ClienteCentroCustoDropDown[]>();
  const [funcionariosDropdown, setFuncionariosDropdown] = useState<FuncionarioDropDown[]>();
  const [pesquisaSatisfacaoDropdown, setPesquisaSatisfacaoDropdown] = useState<PesquisaSatisfacaoDropDown[]>();

  const [registroAtendimentoModal, setRegistroAtendimentoModal] = useState(false);
  const [registroAtendimentoDeleteModal, setRegistroAtendimentoDeleteModal] = useState(false);
  const [pesquisaSatisfacaoModal, setPesquisaSatisfacaoModal] = useState(false);
  const [pesquisaSatisfacaoEditModal, setPesquisaSatisfacaoEditModal] = useState(false);
  const [pesquisaSatisfacaoDeleteModal, setPesquisaSatisfacaoDeleteModal] = useState(false);
  const [arquivoDiversoModal, setArquivoDiversoModal] = useState(false);
  const [arquivoDiversoDeleteModal, setArquivoDiversoDeleteModal] = useState(false);

  const [selectedRegistroAtendimento, setSelectedRegistroAtendimento] = useState<AssistenciaTecnicaAtendimentoLista>();
  const [selectedPesquisaSatisfacao, setSelectedPesquisaSatisfacao] = useState<AssistenciaTecnicaPesquisaLista>();
  const [selectedArquivoDiverso, setSelectedArquivoDiverso] = useState<AssistenciaTecnicaArquivoLista>();

  const [itensPesquisaCliente, setItensPesquisaCliente] = useState<ItemPesquisaClienteLista[]>();

  const hookForm = useForm<AssistenciaTecnicaForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(AssistenciaTecnicaSchema),
  });

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: e => pushErrorNotification("Erro", [e]),
  });

  const [codigoSistema, taxa, taxaCliente, categorias] = hookForm.watch(['codigo', 'taxa', 'taxaCliente', 'categorias']);

  const pagination = useAssistenciaTecnicaCadastroPagination();

  const funcionariosResponsavelDropdown = useMemo(() => {
    if (funcionariosDropdown === undefined) return null;
    return funcionariosDropdown.filter(x => {
      if (selectedRegistroAtendimento) {
        if (x.idFuncionario === selectedRegistroAtendimento.idFuncionario) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [
    selectedRegistroAtendimento,
    funcionariosDropdown,
  ]);

  const reportOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Assistência Básico',
      },
      {
        id: 2,
        label: 'Assistência Completo',
      },
    ];
  }, [
  ]);

  const buttonOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Novo',
      },
      {
        id: 2,
        label: 'Excluir',
      }
    ];
  }, [
  ]);

  const tipoOptions = useMemo(() => {
    return [
      { id: 0, label: 'Interna' },
      { id: 1, label: 'Externa' },
    ];
  }, [
  ]);

  const statusAssistenciaOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Aberta',
        style: {
          backgroundColor: '#fde69a',
          color: '#6b5900',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: 'none',
          borderRadius: '2px',
        },
      },
      {
        id: 1,
        label: 'Encerrada',
        style: {
          backgroundColor: '#f8c8cb',
          color: '#7a2e31',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: 'none',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Baixa',
        style: {
          backgroundColor: '#c6c6c6',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 1,
        label: 'Média',
        style: {
          backgroundColor: '#f8cb00',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 2,
        label: 'Alta',
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 3,
        label: 'Paralisada',
        style: {
          backgroundColor: '#428bca',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  const procedenteOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Aguardando Vistoria',
        style: {
          backgroundColor: '#c6c6c6',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 1,
        label: 'Sim',
        style: {
          backgroundColor: '#45b6af',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 2,
        label: 'Não',
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  const taxaOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Sim',
        style: {
          backgroundColor: '#45b6af',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 0,
        label: 'Não',
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  const resolvidoOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Aguardando Reparo',
        style: {
          backgroundColor: '#c6c6c6',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 3,
        label: 'Reparo Iniciado',
        style: {
          backgroundColor: '#f8cb00',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 1,
        label: 'Sim',
        style: {
          backgroundColor: '#45b6af',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 2,
        label: 'Não',
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  /**
   * Funcoes de carregamento.
   */

  const loadPesquisaSatisfacaoDropDown = useCallback(async () => {
    try {
      const results = await getPesquisaSatisfacaoDropDownUseCase
        .getPesquisaSatisfacaoDropDown();
      setPesquisaSatisfacaoDropdown(results);
    } catch (e: unknown) {
      setPesquisaSatisfacaoDropdown(undefined);
      throw new Error('Falha ao buscar dados para dropdowns da pesquisa de satisfação dos clientes!');
    }
  }, [
    getPesquisaSatisfacaoDropDownUseCase,
  ]);

  const loadAssistenciaTecnica = useCallback(async (id?: number, paramClientesDropDown?: ClienteCentroCustoDropDown[]) => {
    try {
      const filteredIdAssistenciaTecnica = id ?? idAssistenciaTecnica;

      const result = await getAssistenciaTecnicaByIdUseCase
        .getAssistenciaTecnicaById(filteredIdAssistenciaTecnica);

      hookForm.reset();
      hookForm.setValue('codigo', result.codigo?.toString() ?? null);
      hookForm.setValue('tipo', result.tipoAssistencia ?? null);
      hookForm.setValue('centroCusto', result.idCentroCusto ?? null);
      hookForm.setValue('cliente', result.descCliente ?? null);
      hookForm.setValue('unidade', result.local ?? null);
      hookForm.setValue('dataAbertura', result.data ? dayjs.utc(result.data).local().toDate() : null);
      hookForm.setValue('contato', result.contato ?? null);
      hookForm.setValue('previsaoRealizacaoVistoria', result.dataInicio ? dayjs.utc(result.dataInicio).local().toDate() : null);
      hookForm.setValue('previsaoTerminoAssistencia', result.dataTermino ? dayjs.utc(result.dataTermino).local().toDate() : null);
      hookForm.setValue('prioridade', result.prioridade ?? null);
      hookForm.setValue('reclamacao', result.reclamacao ?? null);
      hookForm.setValue('procedente', result.precedente ?? null);
      hookForm.setValue('dataReparo', result.dataReparo ? dayjs.utc(result.dataReparo).local().toDate() : null);
      hookForm.setValue('valorTaxa', result.valorTaxa ?? null);
      hookForm.setValue('resolvido', result.resolvido ?? null);
      hookForm.setValue('dataEncerramento', result.dataEncerramento ? dayjs.utc(result.dataEncerramento).local().toDate() : null);
      hookForm.setValue('observacoes', result.observacao ?? null);
      hookForm.setValue('custo', result.custo ?? null);
      hookForm.setValue('nroChamado', result.nroChamado ?? null);
      hookForm.setValue('acoesCorretivas', result.acaoCorretiva ?? null);
      hookForm.setValue('clienteCadastrado', result.clienteCadastrado ?? false);
      hookForm.setValue('statusAssistencia', result.statusAssistencia ?? 0);

      const clientes = paramClientesDropDown ?? clientesDropdown;
      if (clientes && clientes.length > 0) {
        requestAnimationFrame(() => {
          setFilteredClientesDropdown(
            clientes.filter(c => c.idCentroCusto === result.idCentroCusto)
          );
        });
        const selectedCliente = clientes.find(c => c.idCliente === result.idCliente);
        hookForm.setValue('idClienteCentroCusto', selectedCliente?.idClienteCentroCusto ?? null);
      }

      if (result.taxaCheck !== null) {
        hookForm.setValue('taxa', result.taxaCheck === true ? 1 : 0);
      } else {
        hookForm.resetField('taxa');
      }

      if (result.taxaClienteCheck !== null) {
        hookForm.setValue('taxaCliente', result.taxaClienteCheck === true ? 1 : 0);
      } else {
        hookForm.resetField('taxaCliente');
      }
      setFillCliente(result.clienteCadastrado !== null ? !result.clienteCadastrado : false);

      setAssistenciaTecnica(result);
    } catch (e: unknown) {
      setAssistenciaTecnica(undefined);
      throw new Error('Falha ao carregar assistência técnica!');
    }
  }, [
    idAssistenciaTecnica,
    getAssistenciaTecnicaByIdUseCase,
    clientesDropdown,
    hookForm,
  ]);

  const loadAssistenciaTecnicaRegistrosAtendimento = useCallback(async (fs?: LoadAssistenciaTecnicaRegistrosAtendimentoFuncState) => {
    try {
      const filteredIdAssistenciaTecnica = evaluateParamValue(fs?.id, idAssistenciaTecnica);

      const filteredPage = fs?.page ?? pagination.registroAtendimentos.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.registroAtendimentos.rowsPerPage;

      await getAllAssistenciaRegistroAtendimentoUseCase
        .getAllAssistenciaRegistroAtendimento(
          filteredIdAssistenciaTecnica,
          filteredPage,
          filteredRowsPerPage,
        );

      setRegistrosAtendimentos(getAllAssistenciaRegistroAtendimentoUseCase.items);
      pagination.registroAtendimentosSetter.setTotalItems(getAllAssistenciaRegistroAtendimentoUseCase.totalItems);
    } catch (e: unknown) {
      setRegistrosAtendimentos(undefined);
      throw new Error('Falha ao carregar assistência técnica!');
    }
  }, [
    idAssistenciaTecnica,
    getAllAssistenciaRegistroAtendimentoUseCase,
    pagination,
  ]);

  const loadAssistenciaTecnicaPesquisasSatisfacao = useCallback(async (fs?: LoadAssistenciaTecnicaPesquisasSatisfacaoFuncState) => {
    try {
      const filteredIdAssistenciaTecnica = evaluateParamValue(fs?.id, idAssistenciaTecnica);

      const filteredPage = fs?.page ?? pagination.pesquisaSatisfacao.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.pesquisaSatisfacao.rowsPerPage;

      await getAllAssistenciaPesquisaSatisfacaoUseCase
        .getAllAssistenciaPesquisaSatisfacao(
          filteredIdAssistenciaTecnica,
          filteredPage,
          filteredRowsPerPage,
        );

      setPesquisasSatisfacao(getAllAssistenciaPesquisaSatisfacaoUseCase.items);
      pagination.pesquisaSatisfacaoSetter.setTotalItems(getAllAssistenciaPesquisaSatisfacaoUseCase.totalItems);
    } catch (e: unknown) {
      setPesquisasSatisfacao(undefined);
      throw new Error('Falha ao carregar assistência técnica!');
    }
  }, [
    idAssistenciaTecnica,
    getAllAssistenciaPesquisaSatisfacaoUseCase,
    pagination,
  ]);

  const loadAssistenciaTecnicaArquivos = useCallback(async (fs?: LoadAssistenciaTecnicaArquivosFuncState) => {
    try {
      const filteredIdAssistenciaTecnica = evaluateParamValue(fs?.id, idAssistenciaTecnica);

      const filteredPage = fs?.page ?? pagination.arquivos.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.arquivos.rowsPerPage;

      await getAllAssistenciaArquivoDiversoUseCase
        .getAllAssistenciaArquivoDiverso(
          filteredIdAssistenciaTecnica,
          filteredPage,
          filteredRowsPerPage,
        );

      setArquivos(getAllAssistenciaArquivoDiversoUseCase.items);
      pagination.arquivosSetter.setTotalItems(getAllAssistenciaArquivoDiversoUseCase.totalItems);
    } catch (e: unknown) {
      setArquivos(undefined);
      throw new Error('Falha ao carregar assistência técnica!');
    }
  }, [
    idAssistenciaTecnica,
    getAllAssistenciaArquivoDiversoUseCase,
    pagination,
  ]);

  const loadAssistenciaTecnicaCategorias = useCallback(async (fs?: LoadAssistenciaTecnicaCategoriasFuncState) => {
    try {
      const filteredIdAssistenciaTecnica = evaluateParamValue(fs?.id, idAssistenciaTecnica);

      const results = await getAllAssistenciaCategoriaUseCase
        .getAllAssistenciaCategoria(
          filteredIdAssistenciaTecnica,
        );

      hookForm.setValue('categorias', results.map(c => ({
        idCategoriaAssistencia: c.idCategoriaAssistencia,
        nomeCategoria: c.nomeCategoria ?? '',
        ativo: c.ativo,
      })));

    } catch (e: unknown) {
      hookForm.resetField('categorias');
      throw new Error('Falha ao carregar assistência técnica!');
    }
  }, [
    idAssistenciaTecnica,
    getAllAssistenciaCategoriaUseCase,
    hookForm,
  ]);

  const loadItensPesquisaCliente = useCallback(async (idPesquisaSatisfacaoCliente: number) => {
    try {
      const result = await getAllItemPesquisaClienteUseCase
        .getAllItemPesquisaCliente(idPesquisaSatisfacaoCliente);
      setItensPesquisaCliente(result);
    } catch (e: unknown) {
      setItensPesquisaCliente(undefined);
      throw new Error('Falha ao buscar itens da pesquisa de satisfação dos clientes!');
    }
  }, [
    getAllItemPesquisaClienteUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const paramIdAssistenciaTecnica = params.get('id');
      const locationIdAssistenciaTecnica = paramIdAssistenciaTecnica
        ? parseInt(paramIdAssistenciaTecnica, 10)
        : undefined;

      let clientes: ClienteCentroCustoDropDown[] = [];

      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadPesquisaSatisfacaoDropDown();

      await loadClienteCentroCustoDropDown(dt => {
        clientes = dt;
        setClientesDropdown(dt)
        setFilteredClientesDropdown([]);
      });

      if (locationIdAssistenciaTecnica === undefined || isNaN(locationIdAssistenciaTecnica)) {
        hookForm.reset({ tipo: 0, prioridade: 0, procedente: 0, resolvido: 0, clienteCadastrado: true, statusAssistencia: 0 });
        setFillCliente(false);
        await loadAssistenciaTecnicaCategorias({ id: -1 });
      } else {
        setIdAssistenciaTecnica(locationIdAssistenciaTecnica);

        await loadAssistenciaTecnica(locationIdAssistenciaTecnica, clientes);

        await loadAssistenciaTecnicaRegistrosAtendimento({ id: locationIdAssistenciaTecnica });

        await loadAssistenciaTecnicaPesquisasSatisfacao({ id: locationIdAssistenciaTecnica });

        await loadAssistenciaTecnicaArquivos({ id: locationIdAssistenciaTecnica });

        await loadAssistenciaTecnicaCategorias({ id: locationIdAssistenciaTecnica });
      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      let clientes: ClienteCentroCustoDropDown[] = [];

      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadPesquisaSatisfacaoDropDown();

      await loadClienteCentroCustoDropDown(dt => {
        clientes = dt;
        setClientesDropdown(dt)
        setFilteredClientesDropdown([]);
      });

      if (idAssistenciaTecnica !== undefined) {
        await loadAssistenciaTecnica(undefined, clientes);

        await loadAssistenciaTecnicaRegistrosAtendimento();

        await loadAssistenciaTecnicaPesquisasSatisfacao();

        await loadAssistenciaTecnicaArquivos();
      }

      await loadAssistenciaTecnicaCategorias();

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    idAssistenciaTecnica,
    loadCentroCustoDropDown,
    loadClienteCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadPesquisaSatisfacaoDropDown,
    loadAssistenciaTecnica,
    loadAssistenciaTecnicaRegistrosAtendimento,
    loadAssistenciaTecnicaPesquisasSatisfacao,
    loadAssistenciaTecnicaArquivos,
    loadAssistenciaTecnicaCategorias,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/AssistenciaTecnica_lista' });
  }, [
    backOrNavigate,
  ]);

  const handleAssistenciaTecnicaSubmit = useCallback(async (form: AssistenciaTecnicaForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (idAssistenciaTecnica) {
        await updateAssistenciaTecnicaUseCase.updateAssistenciaTecnica(
          idAssistenciaTecnica,
          form.codigo,
          form.tipo,
          form.centroCusto,
          form.cliente,
          form.idClienteCentroCusto,
          form.unidade,
          form.dataAbertura,
          form.contato,
          form.previsaoRealizacaoVistoria,
          form.prioridade,
          form.previsaoTerminoAssistencia,
          form.reclamacao,
          form.procedente,
          form.dataReparo,
          form.taxa,
          form.taxaCliente,
          form.valorTaxa,
          form.resolvido,
          form.dataEncerramento,
          form.observacoes,
          form.custo,
          form.nroChamado,
          form.acoesCorretivas,
          form.clienteCadastrado,
          form.statusAssistencia,
          form.categorias,
        );

        await loadAssistenciaTecnica();

        await loadAssistenciaTecnicaCategorias();
      } else {
        var createdIdAssistenciaTecnica = await createAssistenciaTecnicaUseCase.createAssistenciaTecnica(
          form.codigo,
          form.tipo,
          form.centroCusto,
          form.cliente,
          form.idClienteCentroCusto,
          form.unidade,
          form.dataAbertura,
          form.contato,
          form.previsaoRealizacaoVistoria,
          form.prioridade,
          form.previsaoTerminoAssistencia,
          form.reclamacao,
          form.procedente,
          form.dataReparo,
          form.taxa,
          form.taxaCliente,
          form.valorTaxa,
          form.resolvido,
          form.dataEncerramento,
          form.observacoes,
          form.custo,
          form.nroChamado,
          form.acoesCorretivas,
          form.clienteCadastrado,
          form.statusAssistencia,
          form.categorias,
        );

        setIdAssistenciaTecnica(createdIdAssistenciaTecnica);

        const params = new URLSearchParams(window.location.search);
        params.set("id", createdIdAssistenciaTecnica.toString());

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, "", newUrl);

        await loadAssistenciaTecnica(createdIdAssistenciaTecnica);

        await loadAssistenciaTecnicaCategorias({ id: createdIdAssistenciaTecnica });
      }

      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao salvar assistência técnica!"));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    idAssistenciaTecnica,
    createAssistenciaTecnicaUseCase,
    updateAssistenciaTecnicaUseCase,
    loadAssistenciaTecnica,
    loadAssistenciaTecnicaCategorias,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleRegistroAtendimentoOpenState = useCallback((idAssistenciaAtendimento: number | null, open: boolean) => {
    if (open) {
      if (idAssistenciaAtendimento && registrosAtendimentos) {
        const selectedRegistroAtendimento = registrosAtendimentos.find(r => r.idAssistenciaAtendimento === idAssistenciaAtendimento);
        setSelectedRegistroAtendimento(selectedRegistroAtendimento);
      } else {
        setSelectedRegistroAtendimento(undefined);
      }
    }
    setRegistroAtendimentoModal(open);
  }, [
    registrosAtendimentos,
  ]);

  const handleRegistroAtendimentoSubmit = useCallback(async (form: RegistroAtendimentoForm) => {
    if (idAssistenciaTecnica) {
      try {
        setSaving(true);
        if (selectedRegistroAtendimento) {
          await updateAssistenciaRegistroAtendimentoUseCase.updateAssistenciaRegistroAtendimento(
            idAssistenciaTecnica,
            selectedRegistroAtendimento.idAssistenciaAtendimento,
            form.tipo,
            form.descricao,
            form.dataInicio,
            form.dataTermino,
            form.observacoes,
            form.responsavelConstrutora,
            form.responsavelCliente,
          );
        } else {
          await createAssistenciaRegistroAtendimentoUseCase.createAssistenciaRegistroAtendimento(
            idAssistenciaTecnica,
            form.tipo,
            form.descricao,
            form.dataInicio,
            form.dataTermino,
            form.observacoes,
            form.responsavelConstrutora,
            form.responsavelCliente,
          );
        }

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');

        await loadAssistenciaTecnicaRegistrosAtendimento();

        setSelectedRegistroAtendimento(undefined);
        setRegistroAtendimentoModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao salvar registro de atendimento!"));
      } finally {
        setSaving(false);
      }
    }
  }, [
    idAssistenciaTecnica,
    selectedRegistroAtendimento,
    updateAssistenciaRegistroAtendimentoUseCase,
    createAssistenciaRegistroAtendimentoUseCase,
    loadAssistenciaTecnicaRegistrosAtendimento,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handleRegistroAtendimentoDeleteOpenState = useCallback((idAssistenciaAtendimento: number | null, open: boolean) => {
    if (open) {
      if (idAssistenciaAtendimento && registrosAtendimentos) {
        const selectedRegistroAtendimento = registrosAtendimentos.find(r => r.idAssistenciaAtendimento === idAssistenciaAtendimento);
        setSelectedRegistroAtendimento(selectedRegistroAtendimento);
      } else {
        setSelectedRegistroAtendimento(undefined);
      }
    }
    setRegistroAtendimentoDeleteModal(open);
  }, [
    registrosAtendimentos,
  ]);

  const handleRegistroAtendimentoDeleteSubmit = useCallback(async () => {
    if (idAssistenciaTecnica && selectedRegistroAtendimento) {
      try {
        setSaving(true);

        await deleteAssistenciaRegistroAtendimentoUseCase.deleteAssistenciaRegistroAtendimento(
          idAssistenciaTecnica,
          selectedRegistroAtendimento.idAssistenciaAtendimento
        );

        pushInfoNotification('Sucesso', 'Registro excluído com sucesso!');

        await loadAssistenciaTecnicaRegistrosAtendimento();

        setSelectedRegistroAtendimento(undefined);
        setRegistroAtendimentoDeleteModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao excluir registro de atendimento!"));
      } finally {
        setSaving(false);
      }
    }
  }, [
    idAssistenciaTecnica,
    selectedRegistroAtendimento,
    deleteAssistenciaRegistroAtendimentoUseCase,
    pushInfoNotification,
    loadAssistenciaTecnicaRegistrosAtendimento,
    pushErrorNotification,
  ]);

  const handlePesquisaSatisfacaoOpenState = useCallback((open: boolean) => {
    setPesquisaSatisfacaoModal(open);
  }, [
  ]);

  const handlePesquisaSatisfacaoSubmit = useCallback(async (form: PesquisaSatisfacaoForm) => {
    if (idAssistenciaTecnica) {
      try {
        setSaving(true);
        if (!form.pesquisa) {
          pushErrorNotification('Erro', 'Nenhum item de pesquisa selecionado para criação!');
          return;
        }

        await createAssistenciaPesquisaSatisfacaoUseCase.createAssistenciaPesquisaSatisfacao(
          idAssistenciaTecnica,
          form.pesquisa,
        );

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');

        setPesquisaSatisfacaoModal(false);

        await loadAssistenciaTecnicaPesquisasSatisfacao();

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao salvar pesquisa de satisfação!"));
      } finally {
        setSaving(false);
      }
    }
  }, [
    idAssistenciaTecnica,
    createAssistenciaPesquisaSatisfacaoUseCase,
    loadAssistenciaTecnicaPesquisasSatisfacao,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handlePesquisaSatisfacaoEditarOpenState = useCallback(async (idPesquisaSatisfacaoCliente: number | null, openState: boolean) => {
    try {
      if (openState) {
        setLoading(true);
        if (idPesquisaSatisfacaoCliente && pesquisasSatisfacao) {
          await loadItensPesquisaCliente(idPesquisaSatisfacaoCliente);
          setSelectedPesquisaSatisfacao(pesquisasSatisfacao.find(p => p.idPesquisaSatisfacaoCliente === idPesquisaSatisfacaoCliente));
        } else {
          setSelectedPesquisaSatisfacao(undefined);
        }
      }
      setPesquisaSatisfacaoEditModal(openState);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao carregar itens da pesquisa de satisfação!"));
    } finally {
      setLoading(false);
    }
  }, [
    pesquisasSatisfacao,
    loadItensPesquisaCliente,
    pushErrorNotification,
  ]);

  const handlePesquisaSatisfacaoClienteEditarSubmit = useCallback(async (form: AssistenciaPesquisaSatisfacaoForm) => {
    if (selectedPesquisaSatisfacao) {
      try {
        setSaving(true);
        setPageSaving(true);

        await updatePesquisaSatisfacaoClienteUseCase.updatePesquisaSatisfacaoCliente(
          selectedPesquisaSatisfacao.idPesquisaSatisfacaoCliente,
          null,
          form.data,
          null,
          form.status,
          null,
          form.itens,
        );

        await loadAssistenciaTecnicaPesquisasSatisfacao();

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');

        setPesquisaSatisfacaoEditModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao criar pesquisa de satisfação do cliente!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedPesquisaSatisfacao,
    updatePesquisaSatisfacaoClienteUseCase,
    loadAssistenciaTecnicaPesquisasSatisfacao,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handlePesquisaSatisfacaoDeleteOpenState = useCallback((idPesquisaSatisfacaoCliente: number | null, open: boolean) => {
    if (open) {
      if (idPesquisaSatisfacaoCliente && pesquisasSatisfacao) {
        const i = pesquisasSatisfacao.find(r => r.idPesquisaSatisfacaoCliente === idPesquisaSatisfacaoCliente);
        setSelectedPesquisaSatisfacao(i);
      } else {
        setSelectedPesquisaSatisfacao(undefined);
      }
    }
    setPesquisaSatisfacaoDeleteModal(open);
  }, [
    pesquisasSatisfacao,
  ]);

  const handlePesquisaSatisfacaoDeleteSubmit = useCallback(async () => {
    if (idAssistenciaTecnica && selectedPesquisaSatisfacao) {
      try {
        setSaving(true);

        await deleteAssistenciaPesquisaSatisfacaoUseCase.deleteAssistenciaPesquisaSatisfacao(
          idAssistenciaTecnica,
          selectedPesquisaSatisfacao.idPesquisaSatisfacaoCliente,
        );

        pushInfoNotification('Sucesso', 'Registro excluído com sucesso!');

        await loadAssistenciaTecnicaPesquisasSatisfacao();

        setSelectedPesquisaSatisfacao(undefined);
        setPesquisaSatisfacaoDeleteModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao excluir pesquisa de satisfação!"));
      } finally {
        setSaving(false);
      }
    }
  }, [
    idAssistenciaTecnica,
    selectedPesquisaSatisfacao,
    deleteAssistenciaPesquisaSatisfacaoUseCase,
    pushInfoNotification,
    loadAssistenciaTecnicaPesquisasSatisfacao,
    pushErrorNotification,
  ]);

  const handleArquivoDiversoOpenState = useCallback((idAssistenciaArquivo: number | null, open: boolean) => {
    if (open) {
      if (idAssistenciaArquivo && arquivos) {
        const i = arquivos.find(r => r.idAssistenciaArquivo === idAssistenciaArquivo);
        setSelectedArquivoDiverso(i);
      } else {
        setSelectedArquivoDiverso(undefined);
      }
    }
    setArquivoDiversoModal(open);
  }, [
    arquivos,
  ]);

  const handleArquivoDiversoSubmit = useCallback(async (form: ArquivoDiversoForm) => {
    if (idAssistenciaTecnica) {
      try {
        setSaving(true);
        setPageSaving(true);

        if (selectedArquivoDiverso) {
          await updateAssistenciaArquivoDiversoUseCase.updateAssistenciaArquivoDiverso(
            idAssistenciaTecnica,
            selectedArquivoDiverso.idAssistenciaArquivo,
            form.descricao,
            form.arquivo,
            form.limparArquivo,
          );
        } else {
          await createAssistenciaArquivoDiversoUseCase.createAssistenciaArquivoDiverso(
            idAssistenciaTecnica,
            form.descricao,
            form.arquivo,
          );
        }

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');

        setSelectedArquivoDiverso(undefined);
        setArquivoDiversoModal(false);

        await loadAssistenciaTecnicaArquivos();

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao salvar arquivo diverso!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idAssistenciaTecnica,
    selectedArquivoDiverso,
    createAssistenciaArquivoDiversoUseCase,
    updateAssistenciaArquivoDiversoUseCase,
    loadAssistenciaTecnicaArquivos,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleArquivoDiversoDeleteOpenState = useCallback((idAssistenciaArquivo: number | null, open: boolean) => {
    if (open) {
      if (idAssistenciaArquivo && arquivos) {
        const i = arquivos.find(r => r.idAssistenciaArquivo === idAssistenciaArquivo);
        setSelectedArquivoDiverso(i);
      } else {
        setSelectedArquivoDiverso(undefined);
      }
    }
    setArquivoDiversoDeleteModal(open);
  }, [
    arquivos,
  ]);

  const handleArquivoDiversoDeleteSubmit = useCallback(async () => {
    if (idAssistenciaTecnica && selectedArquivoDiverso) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteAssistenciaArquivoDiversoUseCase.deleteAssistenciaArquivoDiverso(
          idAssistenciaTecnica,
          selectedArquivoDiverso.idAssistenciaArquivo,
        );

        pushInfoNotification('Sucesso', 'Registro excluído com sucesso!');

        await loadAssistenciaTecnicaArquivos();

        setSelectedArquivoDiverso(undefined);
        setArquivoDiversoDeleteModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao excluir arquivo diverso!"));
      } finally {
        setPageSaving(false);
        setSaving(false);
      }
    }
  }, [
    idAssistenciaTecnica,
    selectedArquivoDiverso,
    deleteAssistenciaArquivoDiversoUseCase,
    pushInfoNotification,
    loadAssistenciaTecnicaArquivos,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleArquivoDiversoDownload = useCallback(async (idAssistenciaArquivo: number) => {
    if (idAssistenciaTecnica) {
      try {
        setLoading(true);

        const nomeArquivo = arquivos?.find(a => a.idAssistenciaArquivo === idAssistenciaArquivo)?.nome
          ?? `arquivo_${idAssistenciaArquivo}`;

        await downloadAssistenciaArquivoDiversoUseCase.downloadAssistenciaArquivoDiverso(
          idAssistenciaTecnica,
          idAssistenciaArquivo,
          nomeArquivo,
        );

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao baixar arquivo!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idAssistenciaTecnica,
    arquivos,
    downloadAssistenciaArquivoDiversoUseCase,
    pushErrorNotification,
  ]);


  const handleFillClienteChange = useCallback((value: boolean) => {
    hookForm.setValue('clienteCadastrado', !value);
    hookForm.resetField('idClienteCentroCusto');
    hookForm.resetField('cliente');
    setFillCliente(value);
  }, [
    hookForm,
  ]);

  const handleLimparTaxa = useCallback(() => {
    hookForm.resetField('taxa');
    hookForm.resetField('taxaCliente');
    hookForm.resetField('valorTaxa');
  }, [
    hookForm,
  ]);

  const handleTaxaChange = useCallback((value: number | null, prevValue: number | null) => {
    hookForm.resetField('taxaCliente');
  }, [
    hookForm,
  ]);

  const handleTaxaClienteChange = useCallback((value: number | null, prevValue: number | null) => {
    hookForm.resetField('valorTaxa');
  }, [
    hookForm,
  ]);

  const handleCategoriaChange = useCallback((idCategoriaAssistencia: number, ativo: boolean) => {
    const categorias = hookForm.getValues('categorias');
    if (categorias && categorias.length > 0) {
      const categoriaIndex = categorias.findIndex(c => c.idCategoriaAssistencia === idCategoriaAssistencia);
      if (categoriaIndex !== -1) {
        categorias[categoriaIndex].ativo = ativo;
        hookForm.setValue(`categorias`, [...categorias]);
      }
    }
  }, [
    hookForm,
  ]);

  const handleCentroCustoChange = useCallback((centroCusto: string | number | null) => {
    try {
      let idCentroCusto = 0;

      if (typeof centroCusto === 'string') {
        idCentroCusto = parseInt(centroCusto);
      } else if (typeof centroCusto === 'number') {
        idCentroCusto = centroCusto;
      }

      if (idCentroCusto) {
        if (clientesDropdown && clientesDropdown.length > 0) {
          const clientes = clientesDropdown.filter(c => c.idCentroCusto === idCentroCusto);
          setFilteredClientesDropdown(clientes);
          hookForm.resetField('idClienteCentroCusto');
          hookForm.resetField('cliente');
        }
      } else {
        setFilteredClientesDropdown([]);
        hookForm.resetField('idClienteCentroCusto');
        hookForm.resetField('cliente');
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao selecionar centro de custo!"));
    }
  }, [
    clientesDropdown,
    hookForm,
    pushErrorNotification,
  ]);

  const handleRegistroAtendimentosPageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaCadastroPagination.getState().registroAtendimentosSetter.setPage(page + 1);
      await loadAssistenciaTecnicaRegistrosAtendimento({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar registros de atendimento!"));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadAssistenciaTecnicaRegistrosAtendimento,
  ]);

  const handleRegistroAtendimentosRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaCadastroPagination.getState().registroAtendimentosSetter.setPage(1);
      useAssistenciaTecnicaCadastroPagination.getState().registroAtendimentosSetter.setRowsPerPage(rowsPerPage);
      await loadAssistenciaTecnicaRegistrosAtendimento({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar registros de atendimento!"));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadAssistenciaTecnicaRegistrosAtendimento,
  ]);

  const handlePesquisaSatisfacaoPageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaCadastroPagination.getState().pesquisaSatisfacaoSetter.setPage(page + 1);
      await loadAssistenciaTecnicaPesquisasSatisfacao({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar pesquisas de satisfação!"));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadAssistenciaTecnicaPesquisasSatisfacao,
  ]);

  const handlePesquisaSatisfacaoRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaCadastroPagination.getState().pesquisaSatisfacaoSetter.setPage(1);
      useAssistenciaTecnicaCadastroPagination.getState().pesquisaSatisfacaoSetter.setRowsPerPage(rowsPerPage);
      await loadAssistenciaTecnicaPesquisasSatisfacao({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar pesquisas de satisfação!"));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadAssistenciaTecnicaPesquisasSatisfacao,
  ]);

  const handleArquivosPageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaCadastroPagination.getState().arquivosSetter.setPage(page + 1);
      await loadAssistenciaTecnicaArquivos({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar arquivos!"));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadAssistenciaTecnicaArquivos,
  ]);

  const handleArquivosRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaCadastroPagination.getState().arquivosSetter.setPage(1);
      useAssistenciaTecnicaCadastroPagination.getState().arquivosSetter.setRowsPerPage(rowsPerPage);
      await loadAssistenciaTecnicaArquivos({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar arquivos!"));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadAssistenciaTecnicaArquivos,
  ]);

  const handleOpenChangeHistory = useCallback((
    idTabela: number | null,
    tipoTabela: LogTipoTabela | null,
  ) => {
    if (idTabela && tipoTabela) {
      changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    } else {
      if (idAssistenciaTecnica) {
        changeHistory.handleOpenChangeHistory(idAssistenciaTecnica, LogTipoTabela.ASSISTENCIA_TECNICA, true);
      }
    }
  }, [
    idAssistenciaTecnica,
    changeHistory,
  ]);

  const handleOptionPopperActions = useCallback(async (id: number) => {
    switch (id) {
      case 1: /* Novo */
        try {
          setLoading(true);

          setIdAssistenciaTecnica(undefined);

          setAssistenciaTecnica(undefined);
          setRegistrosAtendimentos(undefined);
          setPesquisasSatisfacao(undefined);
          setArquivos(undefined);
          setSelectedRegistroAtendimento(undefined);
          setSelectedPesquisaSatisfacao(undefined);
          setSelectedArquivoDiverso(undefined);

          hookForm.reset({ tipo: 0, prioridade: 0, procedente: 0, resolvido: 0 });
          setFillCliente(false);

          const newUrl = `${window.location.pathname}`;
          window.history.replaceState({}, "", newUrl);

          await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

          await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt));

          await loadPesquisaSatisfacaoDropDown();

          await loadClienteCentroCustoDropDown(dt => {
            setClientesDropdown(dt)
            setFilteredClientesDropdown([]);
          });

          await loadAssistenciaTecnicaCategorias({ id: -1 });

        } catch (e: unknown) {
          pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
        } finally {
          setLoading(false);
        }
        break;

      case 2: /* Excluir */
        if (idAssistenciaTecnica) {
          if (window.confirm('Tem certeza que deseja excluir este registro?')) {
            try {
              setSaving(true);
              setPageSaving(true);

              await deleteAssistenciaTecnicaUseCase.deleteAssistenciaTecnica(
                idAssistenciaTecnica,
              );

              pushInfoNotification('Sucesso', 'Registro excluído com sucesso!');

              backOrNavigate(-1, { alternativeRoute: '/AssistenciaTecnica_lista' });

            } catch (e: unknown) {
              pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
            } finally {
              setSaving(false);
              setPageSaving(false);
            }
          }
        }
        break;
    }
  }, [
    idAssistenciaTecnica,
    hookForm,
    deleteAssistenciaTecnicaUseCase,
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadPesquisaSatisfacaoDropDown,
    loadClienteCentroCustoDropDown,
    loadAssistenciaTecnicaCategorias,
    pushInfoNotification,
    pushErrorNotification,
    backOrNavigate,
    setPageSaving,
  ]);

  const handleReportPopperActions = useCallback(async (id: number) => {
    if (idAssistenciaTecnica) {
      switch (id) {
        case 1: /* Assistência Básico */
          try {
            setLoading(true);

            await startReportViewerProcess({
              fetchCallback: async (requiredMimeType) =>
                await getReportAssistenciaTecnicaBasicoUseCase.getReportAssistenciaTecnicaBasico(
                  requiredMimeType,
                  idAssistenciaTecnica,
                ),
            });

          } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
          } finally {
            setLoading(false);
          }
          break;

        case 2: /* Assistência Completo */
          try {
            setLoading(true);

            await startReportViewerProcess({
              fetchCallback: async (requiredMimeType) =>
                await getReportAssistenciaTecnicaCompletoUseCase.getReportAssistenciaTecnicaCompleto(
                  requiredMimeType,
                  idAssistenciaTecnica,
                ),
            });

          } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
          } finally {
            setLoading(false);
          }
          break;
      }
    }
  }, [
    idAssistenciaTecnica,
    getReportAssistenciaTecnicaBasicoUseCase,
    getReportAssistenciaTecnicaCompletoUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    idAssistenciaTecnica,
    fillCliente,
    assistenciaTecnica: assistenciaTecnica ?? null,
    registrosAtendimentos: registrosAtendimentos ?? [],
    pesquisasSatisfacao: pesquisasSatisfacao ?? [],
    arquivos: arquivos ?? [],
    categorias: categorias ?? [],
    centroCustoDropdown: centroCustoDropdown ?? [],
    clientesDropdown: filteredClientesDropdown ?? [],
    funcionariosResponsavelDropdown: funcionariosResponsavelDropdown ?? [],
    pesquisaSatisfacaoDropdown: pesquisaSatisfacaoDropdown ?? [],
    registroAtendimentoModal,
    registroAtendimentoDeleteModal,
    pesquisaSatisfacaoModal,
    pesquisaSatisfacaoEditModal,
    pesquisaSatisfacaoDeleteModal,
    arquivoDiversoModal,
    arquivoDiversoDeleteModal,
    selectedRegistroAtendimento: selectedRegistroAtendimento ?? null,
    selectedPesquisaSatisfacao: selectedPesquisaSatisfacao ?? null,
    selectedArquivoDiverso: selectedArquivoDiverso ?? null,
    itensPesquisaCliente: itensPesquisaCliente ?? [],
    hookForm,
    reportViewerState,
    changeHistory,
    codigoSistema,
    taxa,
    taxaCliente,
    pagination,
    reportOptions,
    buttonOptions,
    tipoOptions,
    statusAssistenciaOptions,
    statusOptions,
    procedenteOptions,
    taxaOptions,
    resolvidoOptions,
    handleReload,
    handleBack,
    handleAssistenciaTecnicaSubmit: hookForm.handleSubmit(handleAssistenciaTecnicaSubmit),
    handleRegistroAtendimentoOpenState,
    handleRegistroAtendimentoSubmit,
    handleRegistroAtendimentoDeleteOpenState,
    handleRegistroAtendimentoDeleteSubmit,
    handlePesquisaSatisfacaoOpenState,
    handlePesquisaSatisfacaoSubmit,
    handlePesquisaSatisfacaoEditarOpenState,
    handlePesquisaSatisfacaoClienteEditarSubmit,
    handlePesquisaSatisfacaoDeleteOpenState,
    handlePesquisaSatisfacaoDeleteSubmit,
    handleArquivoDiversoOpenState,
    handleArquivoDiversoSubmit,
    handleArquivoDiversoDeleteOpenState,
    handleArquivoDiversoDeleteSubmit,
    handleArquivoDiversoDownload,
    handleFillClienteChange,
    handleLimparTaxa,
    handleTaxaChange,
    handleTaxaClienteChange,
    handleCategoriaChange,
    handleCentroCustoChange,
    handleRegistroAtendimentosPageChange,
    handleRegistroAtendimentosRowsPerPageChange,
    handlePesquisaSatisfacaoPageChange,
    handlePesquisaSatisfacaoRowsPerPageChange,
    handleArquivosPageChange,
    handleArquivosRowsPerPageChange,
    handleOpenChangeHistory,
    handleOptionPopperActions,
    handleReportPopperActions,
  };
}